import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Ticket as TicketIcon, Eye, Share2 } from 'lucide-react';
import { toast } from 'sonner';

interface Registration {
  id: string;
  status: string;
  ticketId: string;
  ticketName?: string;
  attendeeName?: string;
}

interface MyTicketsListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  registrations: Registration[];
  tickets: Array<{ id: string; name: string }>;
  onSelect: (registrationId: string) => void;
}

export default function MyTicketsListDialog({
  open,
  onOpenChange,
  registrations,
  tickets,
  onSelect,
}: MyTicketsListDialogProps) {
  const getTicketName = (ticketId: string) => {
    return tickets.find((t) => t.id === ticketId)?.name || 'Unknown Ticket';
  };

  const handleShareAll = async () => {
    const text = `I've got ${registrations.length} tickets for this event! Can't wait.`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'My Event Tickets',
          text: text,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(`${text} ${window.location.href}`);
        toast.success('Link copied to clipboard');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeOutside className="sm:max-w-[500px]">
        <DialogHeader className="flex shrink-0 flex-row items-center justify-between space-y-0 p-4 pb-0 pr-12 text-left sm:p-6 sm:pb-0">
          <div className="flex-1">
            <DialogTitle>My Tickets</DialogTitle>
            <DialogDescription className="mt-1">
              {registrations.length > 1
                ? 'Select a ticket to view details.'
                : 'Your registration details for this event.'}
            </DialogDescription>
          </div>
          {registrations.length > 1 && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleShareAll();
              }}
              className="ml-4 h-8"
            >
              <Share2 className="h-3.5 w-3.5 mr-2" />
              Share All
            </Button>
          )}
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
          {registrations.map((reg) => (
            <Card
              key={reg.id}
              className="hover:border-primary/50 transition-colors cursor-pointer group"
              onClick={() => onSelect(reg.id)}
            >
              <CardContent className="p-3 sm:p-4 flex items-center justify-between gap-2 overflow-hidden">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="bg-primary/10 p-2 rounded-full shrink-0 group-hover:bg-primary/20 transition-colors">
                    <TicketIcon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {reg.attendeeName || 'Attendee'}
                      <span className="text-sm font-normal text-muted-foreground ml-2">
                        • {getTicketName(reg.ticketId)}
                      </span>
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge
                        variant={
                          reg.status === 'CONFIRMED'
                            ? 'default'
                            : reg.status === 'ATTENDED'
                              ? 'secondary'
                              : reg.status === 'CANCELLED'
                                ? 'destructive'
                                : 'outline'
                        }
                        className={
                          reg.status === 'CONFIRMED'
                            ? 'bg-green-500 hover:bg-green-600 text-[10px] h-4 px-1 shrink-0'
                            : reg.status === 'ATTENDED'
                              ? 'bg-blue-500 hover:bg-blue-600 text-white text-[10px] h-4 px-1 shrink-0'
                              : 'text-[10px] h-4 px-1 shrink-0'
                        }
                      >
                        {reg.status}
                      </Badge>
                    </div>
                  </div>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="shrink-0 h-8 w-8 text-muted-foreground group-hover:bg-muted group-hover:text-foreground transition-colors"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t p-4 sm:p-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
